import type React from 'react';
import type { TFunction } from 'i18next';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { OptionMarkedLabel } from '@/js/libs/ui/src/V2';

import type { Option } from 'src/types';

export type MarkedOptionLabel = (option: Option<unknown>) => React.ReactNode | string;

interface MarkedOptionLabelBaseProps {
  markedMarker?: string;
  inactiveMarker?: string;
}

interface MarkedOptionLabelPositiveProps extends MarkedOptionLabelBaseProps {
  positive?: boolean;
  warning?: never;
}

interface MarkedOptionLabelWarningProps extends MarkedOptionLabelBaseProps {
  positive?: never;
  warning?: boolean;
}

export type MarkedOptionLabelProps = MarkedOptionLabelPositiveProps | MarkedOptionLabelWarningProps;

/**
 * Custom hook to generate a marked option label based on the option's properties.
 *
 * @param {TFunction} t - The translation function from i18next.
 * @param markedMarker - The marker to use for marked options instead of default.
 * @param inactiveMarker - The marker to use for inactive options instead of default.
 * @param positive - Whether to mark the option as positive.
 * @param warning - Whether to mark the option as a warning.
 * @returns {MarkedOptionLabel} - A function that returns a React node or string based on the option's properties.
 */
export function useMarkedOptionLabel({
  t,
  positive,
  warning,
  markedMarker,
  inactiveMarker,
}: MarkedOptionLabelProps & { t: TFunction }): MarkedOptionLabel {
  const markedMarkerValue = markedMarker ?? t('common:DEFAULT');

  return useCallbackRef((option) => {
    if (option.marked) {
      if (positive) {
        return <OptionMarkedLabel option={option} positive marker={markedMarkerValue} />;
      }
      if (warning) {
        return <OptionMarkedLabel option={option} warning marker={markedMarkerValue} />;
      }
      return <OptionMarkedLabel option={option} informative marker={markedMarkerValue} />;
    }
    if (typeof option.active === 'boolean' && !option.active) {
      return <OptionMarkedLabel option={option} negative marker={inactiveMarker ?? t('common:INACTIVE')} />;
    }
    return option.label;
  });
}
